package com.sweetlime.bingeamigo.presentation.features.notifications

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.statusBarsPadding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.outlined.Favorite
import androidx.compose.material.icons.outlined.PersonAdd
import androidx.compose.material.icons.outlined.PlayCircle
import androidx.compose.material.icons.outlined.Star
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import cafe.adriel.voyager.core.screen.Screen
import cafe.adriel.voyager.navigator.LocalNavigator
import cafe.adriel.voyager.navigator.currentOrThrow
import com.sweetlime.bingeamigo.model.AppNotification
import com.sweetlime.bingeamigo.presentation.features.tvdetail.TvShowDetailScreen
import com.sweetlime.bingeamigo.presentation.features.user.UserProfileScreen
import com.sweetlime.bingeamigo.presentation.ui.BrandColors
import org.koin.compose.viewmodel.koinViewModel
import kotlin.time.Clock
import kotlin.time.ExperimentalTime

object NotificationsScreen : Screen {

    @Composable
    override fun Content() {
        val navigator = LocalNavigator.currentOrThrow
        val viewModel: NotificationsViewModel = koinViewModel()
        val uiState by viewModel.uiState.collectAsStateWithLifecycle()

        LaunchedEffect(Unit) { viewModel.load() }

        Column(
            modifier = Modifier
                .fillMaxSize()
                .background(BrandColors.Background)
        ) {
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .statusBarsPadding()
                    .padding(horizontal = 4.dp, vertical = 4.dp),
                verticalAlignment = Alignment.CenterVertically
            ) {
                IconButton(onClick = { navigator.pop() }) {
                    Icon(
                        imageVector = Icons.AutoMirrored.Filled.ArrowBack,
                        contentDescription = "Back",
                        tint = Color.White
                    )
                }
                Text(
                    text = "Notifications",
                    color = Color.White,
                    style = MaterialTheme.typography.titleLarge,
                    fontWeight = FontWeight.Bold,
                    modifier = Modifier.weight(1f)
                )
                if (uiState.notifications.any { !it.read }) {
                    TextButton(onClick = { viewModel.markAllAsRead() }) {
                        Text("Mark all read", color = BrandColors.Primary, fontSize = 13.sp)
                    }
                }
            }

            when {
                uiState.isLoading -> {
                    Box(Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                        CircularProgressIndicator(color = BrandColors.Primary)
                    }
                }

                uiState.notifications.isEmpty() -> {
                    Box(Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                        Text(
                            text = "No notifications yet",
                            color = BrandColors.TextSecondary,
                            style = MaterialTheme.typography.bodyLarge
                        )
                    }
                }

                else -> {
                    val today = uiState.notifications.filter { it.isToday() }
                    val earlier = uiState.notifications.filter { !it.isToday() }

                    LazyColumn(
                        modifier = Modifier.fillMaxSize(),
                        verticalArrangement = Arrangement.spacedBy(2.dp)
                    ) {
                        if (today.isNotEmpty()) {
                            item {
                                SectionHeader("Today")
                            }
                            items(today, key = { it.id }) { notification ->
                                NotificationCard(
                                    notification = notification,
                                    onClick = {
                                        if (!notification.read) viewModel.markAsRead(notification.id)
                                        handleNavigation(navigator, notification)
                                    }
                                )
                            }
                        }
                        if (earlier.isNotEmpty()) {
                            item {
                                SectionHeader("Earlier")
                            }
                            items(earlier, key = { it.id }) { notification ->
                                NotificationCard(
                                    notification = notification,
                                    onClick = {
                                        if (!notification.read) viewModel.markAsRead(notification.id)
                                        handleNavigation(navigator, notification)
                                    }
                                )
                            }
                        }
                    }
                }
            }
        }
    }
}

@OptIn(ExperimentalTime::class)
private fun AppNotification.isToday(): Boolean {
    val seconds = createdAt?.seconds ?: return false
    val ageMs = Clock.System.now().toEpochMilliseconds() - seconds * 1000L
    return ageMs < 24 * 60 * 60 * 1000L
}

private fun handleNavigation(
    navigator: cafe.adriel.voyager.navigator.Navigator,
    notification: AppNotification
) {
    when (notification.type) {
        AppNotification.TYPE_FOLLOW -> {
            val senderId = notification.senderId ?: return
            navigator.push(UserProfileScreen(senderId))
        }
        AppNotification.TYPE_LIKE -> {
            val senderId = notification.senderId ?: return
            navigator.push(UserProfileScreen(senderId))
        }
        AppNotification.TYPE_NEW_EPISODE -> {
            val showId = notification.entityId?.toIntOrNull() ?: return
            navigator.push(TvShowDetailScreen(tvShowId = showId, tvShowName = ""))
        }
        AppNotification.TYPE_LEVEL_UP -> { /* no navigation for personal milestone */ }
    }
}

@Composable
private fun SectionHeader(title: String) {
    Text(
        text = title,
        color = BrandColors.TextSecondary,
        fontSize = 12.sp,
        fontWeight = FontWeight.SemiBold,
        modifier = Modifier.padding(horizontal = 16.dp, vertical = 8.dp)
    )
}

@OptIn(ExperimentalTime::class)
@Composable
private fun NotificationCard(
    notification: AppNotification,
    onClick: () -> Unit
) {
    val cardBg = if (notification.read) BrandColors.Card else Color(0xFF1F1A0E)

    Row(
        modifier = Modifier
            .fillMaxWidth()
            .clickable(onClick = onClick)
            .background(cardBg)
            .padding(horizontal = 16.dp, vertical = 12.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
        NotificationTypeIcon(notification.type)

        Spacer(Modifier.width(12.dp))

        Column(modifier = Modifier.weight(1f)) {
            Text(
                text = notification.title,
                color = Color.White,
                fontSize = 14.sp,
                fontWeight = if (notification.read) FontWeight.Normal else FontWeight.Bold,
                lineHeight = 18.sp
            )
            Text(
                text = notification.body,
                color = BrandColors.TextSecondary,
                fontSize = 13.sp,
                lineHeight = 17.sp
            )
            Text(
                text = relativeTimeFromSeconds(notification.createdAt?.seconds),
                color = BrandColors.TextSecondary,
                fontSize = 12.sp,
                modifier = Modifier.padding(top = 3.dp)
            )
        }

        if (!notification.read) {
            Spacer(Modifier.width(8.dp))
            Box(
                modifier = Modifier
                    .size(8.dp)
                    .clip(CircleShape)
                    .background(BrandColors.Primary)
            )
        }
    }
}

@Composable
private fun NotificationTypeIcon(type: String) {
    val (icon, color) = when (type) {
        AppNotification.TYPE_FOLLOW      -> Icons.Outlined.PersonAdd to Color(0xFF4A9EFF)
        AppNotification.TYPE_LIKE        -> Icons.Outlined.Favorite to BrandColors.Favorite
        AppNotification.TYPE_NEW_EPISODE -> Icons.Outlined.PlayCircle to BrandColors.Secondary
        AppNotification.TYPE_LEVEL_UP    -> Icons.Outlined.Star to BrandColors.Primary
        else                             -> Icons.Outlined.Star to BrandColors.TextSecondary
    }
    Box(
        modifier = Modifier
            .size(40.dp)
            .clip(CircleShape)
            .background(color.copy(alpha = 0.15f)),
        contentAlignment = Alignment.Center
    ) {
        Icon(
            imageVector = icon,
            contentDescription = null,
            tint = color,
            modifier = Modifier.size(20.dp)
        )
    }
}

@OptIn(ExperimentalTime::class)
private fun relativeTimeFromSeconds(seconds: Long?): String {
    if (seconds == null) return ""
    val diff = Clock.System.now().toEpochMilliseconds() - seconds * 1000L
    val minutes = diff / 60_000
    val hours = minutes / 60
    val days = hours / 24
    return when {
        minutes < 1  -> "Just now"
        minutes < 60 -> "${minutes}m ago"
        hours < 24   -> "${hours}h ago"
        days < 30    -> "${days}d ago"
        else         -> "${days / 30}mo ago"
    }
}